import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';

import { ReactComponent as StatsChevronIcon } from '../../assets/icons/timecard-stats-chevron.svg';
import TimeCardsColumnChanger from '../TimeCardsColumnChanger';
import HiddenStatCardsDropdown from './HiddenStatCardsDropdown';
import TimecardHeaderStatCard from './TimecardHeaderStatCard';
import {
  formatHours,
  HOURS_BREAKDOWN_COLLAPSED_KEY,
  persistHiddenStatCards,
  readHiddenStatCards,
  readStatsCollapsed,
  STAT_CARD_DEFINITIONS,
  STAT_CARD_KEYS,
  toNumber,
} from './timecardHeaderStats.helpers';

export default function TimecardHeaderStats({
  title,
  isSummary,
  payrollHours,
  showTotalPay,
  sideListUsers,
  sideListUserId,
  onSideListUserSelect,
  sideListNavigationDisabled,
}) {
  const [statsCollapsed, setStatsCollapsed] = useState(readStatsCollapsed);
  const [hiddenStatCards, setHiddenStatCards] = useState(readHiddenStatCards);

  const {
    regularHours,
    otHours,
    doubleOtHours,
    ptoHours,
    saturdayHours,
    saturdayOTHours,
    saturdayDoubleOTHours,
    sundayHours,
    sundayOTHours,
    sundayDoubleOTHours,
    totalPay,
  } = payrollHours;

  const overtimeHours = toNumber(otHours) + toNumber(saturdayOTHours) + toNumber(sundayOTHours);
  const doubleTimeHours = toNumber(doubleOtHours)
    + toNumber(saturdayDoubleOTHours)
    + toNumber(sundayDoubleOTHours);
  const totalHours = toNumber(regularHours)
    + toNumber(otHours)
    + toNumber(doubleOtHours)
    + toNumber(ptoHours)
    + toNumber(saturdayHours)
    + toNumber(saturdayOTHours)
    + toNumber(saturdayDoubleOTHours)
    + toNumber(sundayHours)
    + toNumber(sundayOTHours)
    + toNumber(sundayDoubleOTHours);

  const sideListCurrentIndex = useMemo(() => {
    if (!sideListUsers?.length || !sideListUserId) return -1;
    return sideListUsers.findIndex((user) => user.id === sideListUserId);
  }, [sideListUsers, sideListUserId]);

  const showSideListNav = Boolean(
    sideListUsers?.length > 1
    && onSideListUserSelect,
  );

  const onPreviousSideListUser = useCallback(() => {
    if (sideListNavigationDisabled || sideListCurrentIndex <= 0) return;
    onSideListUserSelect(sideListUsers[sideListCurrentIndex - 1]);
  }, [
    onSideListUserSelect,
    sideListCurrentIndex,
    sideListNavigationDisabled,
    sideListUsers,
  ]);

  const onNextSideListUser = useCallback(() => {
    if (
      sideListNavigationDisabled
      || sideListCurrentIndex < 0
      || sideListCurrentIndex >= sideListUsers.length - 1
    ) return;
    onSideListUserSelect(sideListUsers[sideListCurrentIndex + 1]);
  }, [
    onSideListUserSelect,
    sideListCurrentIndex,
    sideListNavigationDisabled,
    sideListUsers,
  ]);

  const onToggleStatsCollapsed = useCallback(() => {
    setStatsCollapsed((collapsed) => {
      const nextCollapsed = !collapsed;
      if (nextCollapsed) {
        window.localStorage.setItem(HOURS_BREAKDOWN_COLLAPSED_KEY, '1');
      } else {
        window.localStorage.removeItem(HOURS_BREAKDOWN_COLLAPSED_KEY);
      }
      return nextCollapsed;
    });
  }, []);

  const onHideStatCard = useCallback((statKey) => {
    setHiddenStatCards((prev) => {
      const next = new Set(prev);
      next.add(statKey);
      persistHiddenStatCards(next);
      return next;
    });
  }, []);

  const onShowStatCard = useCallback((statKey) => {
    setHiddenStatCards((prev) => {
      const next = new Set(prev);
      next.delete(statKey);
      persistHiddenStatCards(next);
      return next;
    });
  }, []);

  const isStatVisible = useCallback(
    (statKey) => !hiddenStatCards.has(statKey),
    [hiddenStatCards],
  );

  const hiddenStatCardOptions = useMemo(() => (
    STAT_CARD_DEFINITIONS.filter((definition) => {
      if (definition.key === STAT_CARD_KEYS.TOTAL_PAY && !showTotalPay) return false;
      return hiddenStatCards.has(definition.key);
    })
  ), [hiddenStatCards, showTotalPay]);

  const allCardsHidden = useMemo(() => (
    STAT_CARD_DEFINITIONS.filter(
      (definition) => definition.key !== STAT_CARD_KEYS.TOTAL_PAY || showTotalPay,
    ).every((definition) => hiddenStatCards.has(definition.key))
  ), [hiddenStatCards, showTotalPay]);

  const onCollapseStatsPanel = useCallback(() => {
    setStatsCollapsed(true);
    window.localStorage.setItem(HOURS_BREAKDOWN_COLLAPSED_KEY, '1');
  }, []);

  const showStatsPanel = !isSummary && !statsCollapsed;

  return (
    <div className="timecard-header-stats-block">
      {title && (
        <div className="timecard-header-stats-title-row">
          <div className="timecard-header-stats-title-group">
            <div className="timecard-header-stats-title">{title}</div>
            {!isSummary && (
              <Tooltip
                title={
                  statsCollapsed
                    ? 'Show hours breakdown panel'
                    : 'Hide hours breakdown panel'
                }
              >
                <button
                  type="button"
                  className={
                    statsCollapsed
                      ? 'timecard-header-stats-toggle is-collapsed'
                      : 'timecard-header-stats-toggle'
                  }
                  aria-label={
                    statsCollapsed
                      ? 'Show hours breakdown panel'
                      : 'Hide hours breakdown panel'
                  }
                  aria-expanded={!statsCollapsed}
                  onClick={onToggleStatsCollapsed}
                >
                  <StatsChevronIcon />
                </button>
              </Tooltip>
            )}
          </div>
          {showSideListNav && (
            <div className="timecard-header-stats-nav">
              <button
                type="button"
                className="team-timecard-toolbar-arrow"
                aria-label="Previous team member"
                disabled={sideListNavigationDisabled || sideListCurrentIndex <= 0}
                onClick={onPreviousSideListUser}
              >
                <LeftOutlined />
              </button>
              <button
                type="button"
                className="team-timecard-toolbar-arrow"
                aria-label="Next team member"
                disabled={
                  sideListNavigationDisabled
                  || sideListCurrentIndex < 0
                  || sideListCurrentIndex >= sideListUsers.length - 1
                }
                onClick={onNextSideListUser}
              >
                <RightOutlined />
              </button>
            </div>
          )}
        </div>
      )}
      <div
        className={
          isSummary
            ? 'timecard-header-stats-row'
            : `timecard-header-stats-row${showStatsPanel ? ' timecard-header-stats-row--with-stats' : ''}`
        }
      >
        {showStatsPanel && (
          <div className="timecard-header-stats">
            {isStatVisible(STAT_CARD_KEYS.REGULAR) && (
              <TimecardHeaderStatCard
                statKey={STAT_CARD_KEYS.REGULAR}
                label="Regular Hours"
                value={formatHours(regularHours)}
                valueClassName="is-blue"
                onHide={onHideStatCard}
              />
            )}
            {isStatVisible(STAT_CARD_KEYS.OT) && (
              <TimecardHeaderStatCard
                statKey={STAT_CARD_KEYS.OT}
                label="OT Hours"
                value={formatHours(overtimeHours)}
                valueClassName="is-green"
                onHide={onHideStatCard}
              />
            )}
            {isStatVisible(STAT_CARD_KEYS.DT_OT) && (
              <TimecardHeaderStatCard
                statKey={STAT_CARD_KEYS.DT_OT}
                label="DT OT Hours"
                value={formatHours(doubleTimeHours)}
                valueClassName="is-amber"
                onHide={onHideStatCard}
              />
            )}
            {isStatVisible(STAT_CARD_KEYS.PTO) && (
              <TimecardHeaderStatCard
                statKey={STAT_CARD_KEYS.PTO}
                label="PTO Hours"
                value={formatHours(ptoHours)}
                valueClassName="is-orange"
                onHide={onHideStatCard}
              />
            )}
            {isStatVisible(STAT_CARD_KEYS.TOTAL_HOURS) && (
              <TimecardHeaderStatCard
                statKey={STAT_CARD_KEYS.TOTAL_HOURS}
                label="Total Hours"
                value={formatHours(totalHours)}
                onHide={onHideStatCard}
              />
            )}
            {showTotalPay && isStatVisible(STAT_CARD_KEYS.TOTAL_PAY) && (
              <TimecardHeaderStatCard
                statKey={STAT_CARD_KEYS.TOTAL_PAY}
                label="Total Pay"
                value={`$${formatHours(totalPay)}`}
                valueClassName="is-total"
                onHide={onHideStatCard}
              />
            )}
            {hiddenStatCardOptions.length > 0 && (
              <HiddenStatCardsDropdown
                hiddenCards={hiddenStatCardOptions}
                onShowStatCard={onShowStatCard}
              />
            )}
            {allCardsHidden && (
              <button
                type="button"
                className="link-button timecard-header-stats-collapse-panel"
                onClick={onCollapseStatsPanel}
              >
                <span className="link-button-text">Collapse panel</span>
              </button>
            )}
          </div>
        )}
        <TimeCardsColumnChanger isSummary={isSummary} />
      </div>
    </div>
  );
}

TimecardHeaderStats.propTypes = {
  title: PropTypes.string,
  isSummary: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  payrollHours: PropTypes.object,
  showTotalPay: PropTypes.bool,
  sideListUsers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  })),
  sideListUserId: PropTypes.string,
  onSideListUserSelect: PropTypes.func,
  sideListNavigationDisabled: PropTypes.bool,
};

TimecardHeaderStats.defaultProps = {
  title: undefined,
  isSummary: false,
  payrollHours: {},
  showTotalPay: false,
  sideListUsers: undefined,
  sideListUserId: undefined,
  onSideListUserSelect: undefined,
  sideListNavigationDisabled: false,
};
